import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sseRef = useRef(null);
  const pollRef = useRef(null);
  const hubRef = useRef(null);
  const loggedOutRef = useRef(false);

  const forceLogout = useCallback(() => {
    if (loggedOutRef.current) return;
    loggedOutRef.current = true;
    if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (hubRef.current) { clearInterval(hubRef.current); hubRef.current = null; }
    setUser(false);
    window.location.href = '/login';
  }, []);

  useEffect(() => {
    checkAuth();
    return () => {
      if (sseRef.current) sseRef.current.close();
      if (pollRef.current) clearInterval(pollRef.current);
      if (hubRef.current) clearInterval(hubRef.current);
    };
  }, []);

  // SSE + Polling for forced logout
  useEffect(() => {
    if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    loggedOutRef.current = false;

    if (user && user.role !== 'master' && user.company_id) {
      // SSE connection for instant logout
      try {
        const es = new EventSource(`${API_URL}/api/auth/events`, { withCredentials: true });
        es.addEventListener('force_logout', () => {
          forceLogout();
        });
        es.onerror = () => {};
        sseRef.current = es;
      } catch (e) {}

      // Aggressive polling every 5s as safety net.
      // Hardening: require TWO consecutive 401s before force-logout. A single
      // 401 can come from a transient backend reload, a request landing
      // mid-deploy, or a momentary cookie hiccup — none of those should kick
      // a real user out (and definitely not kick the WHOLE TENANT out when
      // multiple browsers hit the same window).
      let consecutive401 = 0;
      pollRef.current = setInterval(async () => {
        if (loggedOutRef.current) return;
        try {
          await axios.get(`${API_URL}/api/auth/session-check`, { withCredentials: true });
          consecutive401 = 0; // happy path resets the counter
        } catch (err) {
          if (err.response?.status === 401) {
            consecutive401 += 1;
            if (consecutive401 >= 2) forceLogout();
          } else {
            // 5xx / network errors are NEVER grounds for logout. Just ignore.
            consecutive401 = 0;
          }
        }
      }, 5000);

      // ── Swami Hub real-time revocation poll (30s, foreground-only) ──
      // The Hub tells us when the master pressed Stop on this user's passkey,
      // when the master kicked just this session, or when the user was
      // deleted. Backend forwards to {SWAMI_HUB_URL}/api/auth/check-status
      // with our app api_key. Fail-open on errors.
      //
      // Hardening: require TWO consecutive `active: false` Hub responses
      // before force-logout. A single Hub glitch (rate limit, transient
      // bug, network blip) used to instantly kick the whole tenant. Now it
      // needs the Hub to confirm revocation twice in a row — adds at most
      // ~30s latency on a genuine revocation, but eliminates the tenant-wide
      // false-kick scenario.
      let consecutiveRevoked = 0;
      const hubTick = async () => {
        if (loggedOutRef.current) return;
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        try {
          const { data } = await axios.get(`${API_URL}/api/session/heartbeat`, { withCredentials: true });
          if (data && data.active === false) {
            consecutiveRevoked += 1;
            if (consecutiveRevoked < 2) return; // wait for a second confirmation
            try { await axios.post(`${API_URL}/api/session/force-logout`, {}, { withCredentials: true }); } catch (_e) { /* ignore — we're logging out anyway */ }
            const reasonMsg = data.reason === 'passkey_suspended'
              ? 'Your access has been suspended by your administrator (Swami Hub).'
              : data.reason === 'session_revoked'
                ? 'Your session was ended by your administrator (Swami Hub).'
                : data.reason === 'user_deleted'
                  ? 'Your account has been removed by your administrator (Swami Hub).'
                  : 'Your access was revoked by your administrator (Swami Hub).';
            // Surface via alert so the user knows WHY they got kicked.
            try { alert(reasonMsg); } catch (_e) { /* SSR / sandboxed iframe — skip */ }
            forceLogout();
          } else {
            consecutiveRevoked = 0;
          }
        } catch (_) { /* network hiccup — ignore (fail-open). DO NOT increment counter. */ }
      };
      hubRef.current = setInterval(hubTick, 30000);
      hubTick(); // run once immediately after login
    }

    return () => {
      if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (hubRef.current) { clearInterval(hubRef.current); hubRef.current = null; }
    };
  }, [user, forceLogout]);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback(async (username, password) => {
    const { data } = await axios.post(`${API_URL}/api/auth/login`, { username, password }, { withCredentials: true });
    setUser(data);
    return data;
  }, []);

  const passkeyLogin = useCallback(async (passkey) => {
    const { data } = await axios.post(`${API_URL}/api/auth/passkey-login`, { passkey }, { withCredentials: true });
    return data;
  }, []);

  const register = useCallback(async (regData) => {
    const { data } = await axios.post(`${API_URL}/api/auth/register`, regData, { withCredentials: true });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, passkeyLogin, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  // Return null instead of throwing when no AuthProvider is mounted (e.g. the
  // public CompanySetup wizard which runs OUTSIDE the AuthProvider tree).
  // Callers must defensively check `auth?.user` etc., which they already do.
  return useContext(AuthContext);
}
